import { useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { Alert } from '@/components/ui/Alert'
import { Button } from '@/components/ui/Button'
import { useCourses } from '@/features/courses/hooks/useCourses'
import {
  useInstitutionClasses,
  useRevokeShare,
  useShareCourse,
  useSharedCourses,
} from '../hooks/useInstitution'

/**
 * Partage de cours (spécifications § 2.4). Sans classe choisie, le cours est
 * partagé à tout l'établissement ; la révocation est immédiate.
 */
export function SharedCourseManager() {
  const { t } = useTranslation()
  const [courseId, setCourseId] = useState('')
  const [classId, setClassId] = useState('')

  const { data: shares, isError } = useSharedCourses()
  const { data: classes } = useInstitutionClasses()
  const { data: coursesPage } = useCourses({})
  const share = useShareCourse()
  const revoke = useRevokeShare()

  const courses = coursesPage?.data ?? []

  function handleSubmit(event: FormEvent) {
    event.preventDefault()

    if (courseId === '') return

    share.mutate(
      { course_id: courseId, class_id: classId === '' ? null : classId },
      { onSuccess: () => { setCourseId(''); setClassId('') } }
    )
  }

  return (
    <section aria-labelledby="shares-title" className="flex flex-col gap-4">
      <h2 id="shares-title" className="font-display text-lg font-semibold text-on-surface">
        {t('institution.shares.title')}
      </h2>

      <form onSubmit={handleSubmit} className="flex flex-col gap-3 md:flex-row md:items-end">
        <span className="flex flex-1 flex-col gap-1">
          <label htmlFor="share-course" className="text-sm font-medium text-surface-800">
            {t('institution.shares.course')}
          </label>
          <select
            id="share-course"
            value={courseId}
            onChange={(event) => setCourseId(event.target.value)}
            className="w-full rounded-lg border border-surface-200 px-3 py-2 text-sm text-neutral-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
          >
            <option value="">{t('institution.shares.course_placeholder')}</option>
            {courses.map((course) => (
              <option key={course.id} value={course.id}>
                {course.title}
              </option>
            ))}
          </select>
        </span>

        <span className="flex flex-1 flex-col gap-1">
          <label htmlFor="share-class" className="text-sm font-medium text-surface-800">
            {t('institution.shares.scope')}
          </label>
          <select
            id="share-class"
            value={classId}
            onChange={(event) => setClassId(event.target.value)}
            className="w-full rounded-lg border border-surface-200 px-3 py-2 text-sm text-neutral-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
          >
            <option value="">{t('institution.shares.whole_institution')}</option>
            {(classes ?? []).map((institutionClass) => (
              <option key={institutionClass.id} value={institutionClass.id}>
                {institutionClass.name}
              </option>
            ))}
          </select>
        </span>

        <Button type="submit" disabled={courseId === ''} isLoading={share.isPending}>
          {t('institution.shares.share')}
        </Button>
      </form>

      {isError && <Alert variant="error">{t('institution.shares.load_error')}</Alert>}

      {shares && shares.length === 0 && (
        <p className="rounded-2xl bg-surface-low px-4 py-6 text-center text-sm text-on-surface-variant">
          {t('institution.shares.empty')}
        </p>
      )}

      {shares && shares.length > 0 && (
        <ul className="flex flex-col gap-2">
          {shares.map((entry) => (
            <li
              key={entry.id}
              className="flex flex-wrap items-center gap-3 rounded-2xl bg-surface-low px-4 py-3"
            >
              <span className="flex min-w-0 flex-1 flex-col">
                <span className="truncate font-medium text-on-surface">{entry.title}</span>
                <span className="text-xs text-on-surface-variant">
                  {entry.class_name ?? t('institution.shares.whole_institution')}
                </span>
              </span>

              <Button
                size="sm"
                variant="secondary"
                onClick={() =>
                  revoke.mutate({ courseId: entry.course_id, classId: entry.class_id ?? undefined })
                }
              >
                {t('institution.shares.revoke')}
              </Button>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}
